import { SubscriptionStatus, UserLicenseType, CouchUserGooglePlayData, CouchUserAppleData, CouchUserCoachData, CouchUserCoachTierData, CouchUserCoachSeatData, CouchUserPaddleData } from './data-model/couch-user';
import { UserConsentRecords } from './user-consent-interface';

export interface CouchLoginParams {
  /** The browser's ad-tracking bag, base64url-encoded JSON, sent so the server
   *  can record where this person came from on the ONE-TIME create of their
   *  user document (#5408).
   *
   *  It is optional and untrusted. A client that sends nothing, or sends a
   *  malformed or oversized value, still signs in normally: the server decodes
   *  it defensively and treats any failure as "no tracking data". See
   *  `sanitizeTrackingForAccountCreation` in `shared/src/checkout-tracking.ts`
   *  for what survives, and `design-docs/telemetry.md` for why the value is
   *  written at account creation rather than on every login.
   */
  telemetryData?: string;
};

/** The status `couchLogin` refuses with when this account has been DELETED, so
 * there is nothing to sign in to and rebuilding it is exactly what must not
 * happen (#5136, `df-ab9038b7aa20` — see
 * `design-docs/mobile-app-accounts.md`, "A deleted account is never rebuilt").
 *
 * 410 Gone says the thing existed and does not any more, which is precisely the
 * condition.
 *
 * It is a STATUS CODE and not a message for the reason
 * `STORAGE_WRITE_OBJECT_EXISTS_HTTP_STATUS` gives in `storage-interface.ts`: the
 * client wrapper throws `CloudFunctionHttpError(name, response.status)` without
 * reading the body, so a declared function's `clientMessage` never crosses the
 * wire and the status is the only thing the client can key on.
 *
 * It must NOT be 409, which is taken by the "this server is behind this
 * account's data version" refusal. The two want opposite handling — that one
 * clears itself once the live server catches up, this one never does — and the
 * client has nothing but the status to tell them apart.
 *
 * It lives here, in the shared package both sides already import, so there is
 * exactly one copy. `SERVER_BEHIND_ACCOUNT_VERSION_HTTP_STATUS` is declared
 * separately on each side and has to be kept in step by hand; do not copy that
 * shape for anything new. */
export const ACCOUNT_DELETED_HTTP_STATUS = 410;

export interface CouchLoginResult {
  user: string;
  sessionCookie: string;
  sessionExpires: string;
  roles: string[]; // don't use this directly, use getAuthRole() that will also check for licenseExpires_utc
  licenseExpires_utc?: string; // added by firebase server, so client can predict when license will expire
  trialEnds_utc?: string; // added by firebase server, so client can predict when trial will expire
  canCancelSubscription?: boolean; // don't use this directly, use getCanCancelSubscription() will also check for licenseExpires_utc
  subscriptionStatus?: SubscriptionStatus;
  licenseType?: UserLicenseType;
  payProSubscriptionStatus?: SubscriptionStatus;
  payProSubscriptionId?: string;
  payProInvoiceUrl?: string;
  payProProductId?: string;
  googlePlayData?: CouchUserGooglePlayData;
  appleData?: CouchUserAppleData;
  // Paddle web-subscription block, exposed to the client the same way
  // googlePlayData / appleData are, so the User Profile page can read the
  // stored price id (to show the Type row and drive Switch-to-Yearly) and the
  // trial fields (to show the "cancel anytime" note) and render the Paddle
  // self-service controls (issue #4189). See design-docs/paddle-integration.md
  // ("Self-service management").
  paddleData?: CouchUserPaddleData;
  // Fixed-tier coach subscription block (Phase 2). Exposed to the client
  // the same way googlePlayData/appleData are: the coach-billing dashboard
  // reads the current tier, and the Google Play coach-tier cross-grade
  // reads coachTierData.purchaseToken as the oldPurchaseToken. See
  // design-docs/coach-slot-pricing.md.
  coachTierData?: CouchUserCoachTierData;
  // Paddle per-seat (web) coaching block (Phase 4, #3919). Exposed to the
  // client the same way coachTierData is, so client coach recognition
  // (`isActiveCoach` / `hasActiveCoachSeat`) can see a Paddle seat coach — a
  // seat coach is uncapped, so `coachMaxSlots` is 0 and there is no numeric
  // slot cap to read. See design-docs/paddle-integration.md.
  coachSeatData?: CouchUserCoachSeatData;
  // The COACH-GRANTED access block on this user's own record, present when a
  // coach is paying for this person's Premium (#4393). Exposed the same way the
  // two blocks above are, and for one reason: the Subscribe page has to tell
  // "the coach who was paying for you stopped" apart from "your own
  // subscription expired", and those two land the user on the same paywall with
  // the same expired role. Nothing new leaks — this is the client's own record,
  // and the coach uid inside it is already in their local
  // `coach-relationship/as-client` document.
  //
  // A PRESENT block is not by itself proof of a live coaching relationship:
  // coachDetachClient leaves the block (set to Terminated) in place after a
  // detach. Confirm liveness from the `as-client` document before telling
  // anyone they still have a coach. See design-docs/coach-subscription-lapse.md.
  coachData?: CouchUserCoachData;
  userHex?: string; // needed for couch automatic per-user database
  consents?: UserConsentRecords;
  magicLinkLastSent_utc?: string; // to prevent sending too many magic links in a row
  telemetryData?: { [key: string]: string };
  couchPass?: string; // CouchDB password for direct _session POST (web only)
  coachMaxSlots?: number;
  // The first time this person ever PAID us, on any rail (#4625). Copied
  // straight off the account record's `firstPaidAt_utc`, and exposed here for
  // the same reason `trialEnds_utc` above is: the client needs it at the moment
  // a review prompt might fire, and an extra round trip there would put a
  // network call on the interaction path. Absent when we do not know — which is
  // every account the backfill could not reach — and the gate treats absent as
  // "do not prompt". See design-docs/review-prompts.md.
  firstPaidAt_utc?: string;
}

/// Make sure to edit descriptions table below if you change this!
export enum AuthRole {
  Unknown = 0,
  //Invited = 50, not used anymore
  Paid = 100,
  Vip = 200,
  Mod = 300,
  Dev = 400,
  Officer = 500,
  //Admin = 600, not used for now
  ServerDev = 700,
}

/**
 * Every role's id. A closed union, not `string`, so `authRoleDescriptionsWithTexts` below can
 * demand one texts entry per role and a missing one becomes a compile error rather than a
 * `TypeError` the first time a profile page renders.
 */
export type AuthRoleId = 'unknown' | 'paid' | 'vip' | 'mod' | 'dev' | 'officer' | 'serverdev';

/**
 * The stable, text-free part of a role. The server and tools/ read ONLY this part -- they
 * resolve roles by `id` / `value` and never display `name` or `userExplanation` -- which is
 * why the base table below carries no user-visible words at all.
 *
 * `adminExplanation` is the one piece of English that stays here on purpose: it is shown only
 * on the admin pages, which are the project's standing carve-out from translation.
 */
export interface AuthRoleBase {
  id: AuthRoleId;
  value: AuthRole;
  icon: string;
  adminExplanation: string;
}

/**
 * The two user-visible words for one role, supplied by the caller.
 *
 * Shared code cannot import the client's `LL`, so the client hands these in. The generic `T`
 * carries the caller's string type through: the client passes `LL.authRoles.<id>` (whose members
 * return the branded `TranslatedString`), so the built description comes back branded, with no
 * cast at the call site (#4368).
 */
export interface AuthRoleTexts<T extends string = string> {
  name: () => T;
  userExplanation: () => T;
}

/** A role plus its display text, ready to show to the user. */
export interface AuthRoleDesc<T extends string = string> extends AuthRoleBase {
  name: T;
  userExplanation: T;
}

export const authRoleDescriptions: AuthRoleBase[] = [
  {
    id: 'unknown',
    value: AuthRole.Unknown,
    icon: 'person',
    adminExplanation: 'User whose subscription is not yet paid, or has expired.',
  },
  {
    id: 'paid',
    value: AuthRole.Paid,
    icon: 'premium',
    adminExplanation: 'User pays monthly subscription.',
  },
  {
    id: 'vip',
    value: AuthRole.Vip,
    icon: 'gift',
    adminExplanation:
      'VIP users are given access manually by admins (used for non-admin staff, testers, etc). Includes all benefits of premium subscription and never expires unless manually removed.',
  },
  {
    id: 'mod',
    value: AuthRole.Mod,
    icon: 'ribbon',
    adminExplanation: 'Moderates community contributions. This includes premium subscription automatically.',
  },
  {
    id: 'dev',
    value: AuthRole.Dev,
    icon: 'code-working',
    adminExplanation: 'Special debugging rights for developers',
  },
  {
    id: 'officer',
    value: AuthRole.Officer,
    icon: 'school',
    adminExplanation:
      'Officers can edit operating parameters of the system (workout and meal solvers, official exercises, etc.). Includes moderating rights for community contributions. Also able to edit and delete users.',
  },
  {
    id: 'serverdev',
    value: AuthRole.ServerDev,
    icon: 'cloud',
    adminExplanation: 'Special debugging rights for server developers',
  },
];

export const authRolesById = new Map<string, AuthRoleBase>(authRoleDescriptions.map(d => [d.id, d]) as [string, AuthRoleBase][]);
export const authRolesByValue = new Map<AuthRole, AuthRoleBase>(authRoleDescriptions.map(d => [d.value, d]) as [AuthRole, AuthRoleBase][]);

/**
 * The same roles, with their display text filled in from the caller's translations.
 *
 * The client uses this; the server and tools/ use the text-free `authRoleDescriptions` above.
 * Roles come back in the same order, so a picker built from either list matches.
 */
export function authRoleDescriptionsWithTexts<T extends string>(
  // a mapped type over the closed id union, NOT an index signature -- that is what makes a role
  // added without its words a compile error, the same guarantee exerciseStatStateDescription and
  // BfErrorTexts.skinfoldNames get from mapping over their own unions
  texts: { [roleId in AuthRoleId]: AuthRoleTexts<T> },
): AuthRoleDesc<T>[] {
  return authRoleDescriptions.map(role => ({
    ...role,
    name: texts[role.id].name(),
    userExplanation: texts[role.id].userExplanation(),
  }));
}

export function authRoleFromStrings(roles: string[]): AuthRole {
  if (roles === undefined) {
    return AuthRole.Unknown;
  }

  // get the role with maximum value
  let role = AuthRole.Unknown;
  for (let i = 0; i < roles.length; i++) {
    let rid = roles[i];
    let d = authRolesById.get(rid);
    if (d && d.value > role) {
      role = d.value;
    }
  }

  return role;
}

export function authRoleToStrings(role: AuthRole): string[] {
  let roles = [];
  let d = authRolesByValue.get(role);
  if (d) {
    roles.push(d.id);
  }
  return roles;
}
